import React from "react";

import { FarmResponse } from "models";

import { HomeSegment, HomeStatusCardKind, hasFieldManagementPayout } from "pages/app/home/hero/homeSegment";
import { FieldManagementCard } from "pages/app/home/status-cards/FieldManagementCard";
import { FootprintingCard } from "pages/app/home/status-cards/FootprintingCard";
import { PayoutCard } from "pages/app/home/status-cards/PayoutCard";
import { HomeFieldStats } from "pages/app/home/status-cards/homeFieldStats";
import { useHomeFootprintingSummary } from "pages/app/home/status-cards/useHomeFootprintingSummary";

type HomeStatusCardsProps = {
  farm: FarmResponse;
  segment: HomeSegment;
  cards: HomeStatusCardKind[];
  reportingYear: number;
  /** footprinting forms and the map bar's crops are per calendar year, like the farm card and the footprinting page */
  currentYear: number;
  hasFields: boolean;
  fieldStats: HomeFieldStats;
  fieldStatsLoading: boolean;
  /** the field list failed to load: the field management card and a payout with a field management part can't show their numbers */
  fieldsUnavailable: boolean;
  /** the field practices failed to load: the field management card can't show its numbers */
  fieldStatsUnavailable: boolean;
  /** the farm's feature flags failed to load: whether the payout follows the Soil+ 3 allotment is unknown */
  allotmentUnavailable: boolean;
  /** the field list or the reporting year is still loading: every card shows its skeleton instead of a "no fields" state */
  loading: boolean;
};

export const HomeStatusCards = ({
  farm,
  segment,
  cards,
  reportingYear,
  currentYear,
  hasFields,
  fieldStats,
  fieldStatsLoading,
  fieldsUnavailable,
  fieldStatsUnavailable,
  allotmentUnavailable,
  loading,
}: HomeStatusCardsProps) => {
  // the payout card only needs the footprinting figures in the segments that also show the footprinting card
  const footprinting = useHomeFootprintingSummary(farm, currentYear, { enabled: cards.includes("footprinting") });

  if (!cards.length) return null;

  return (
    <div className="home-status-cards" data-testid="home-status-cards">
      {cards.includes("payout") ? (
        <PayoutCard
          farm={farm}
          segment={segment}
          reportingYear={reportingYear}
          hasFields={hasFields}
          footprinting={footprinting}
          loading={loading}
          unavailable={hasFieldManagementPayout(segment) && (fieldsUnavailable || allotmentUnavailable)}
        />
      ) : null}
      {cards.includes("fieldManagement") ? (
        <FieldManagementCard
          stats={fieldStats}
          hasFields={hasFields}
          loading={loading || fieldStatsLoading}
          unavailable={fieldsUnavailable || fieldStatsUnavailable}
        />
      ) : null}
      {cards.includes("footprinting") ? <FootprintingCard footprinting={footprinting} hasFields={hasFields} loading={loading} /> : null}
    </div>
  );
};
